import { createSelector } from "reselect"
import { get, first, last, chain, find, map, filter } from "lodash"
import moment from "moment"
import { getStartDate, getEndDate, getSelectedDate } from "modules/security-profile/selectors/dates"
import { ANALYZE_FACTORS_TAKE } from "helpers/analyzeConstants"
import { makeComputeDriftFromData } from "helpers/drift"
import { getFactorsMap, getCategories } from "./factors"
import { computeDisplayCategories, computeNonZerosKeys } from "helpers/analyze"

const defaultExposureData = {}
const EMPTY_ARRAY = []

export const getDisabled = state => get(state, "modules.securityProfileContentSet.disabled")

const getRawSelectedCategory = state => get(state, "modules.securityProfileContentSet.selectedCategory")
const getExposureData = state => get(state, "global.data.securityContentSetHistory.data.model.simulation.exposure", defaultExposureData)

function formatExposure(exposure) {
  const count = exposure.length

  return chain(exposure)
    .map(({ date, factors }, index) => {
      // Always return last date
      if (factors.length || index === count - 1) {
        return ({
          date,
          factors: chain(factors)
            .keyBy("id")
            .mapValues("net")
            .value(),
        })
      }

      return null
    })
    .filter(data => data)
    .value()
}

export function computeData(items, exposure) {
  return chain(exposure)
    .map((datum, index) => {
      const { date } = datum
      const result = chain(items)
        .map(item => {
          return ([
            item.id,
            item.fn
              ? item.fn(datum)
              : get(datum, item.field, 0),
          ])
        })
        .filter(([, value]) => !Number.isNaN(value))
        .fromPairs()
        .value()

      result.date = moment(date)
      result.index = index

      return result
    })
    .filter(datum => datum)
    .value()
}

export const getExposure = createSelector(
  [getExposureData],
  (absolute) =>
    formatExposure(absolute),
)

export const getFilteredExposure = createSelector(
  [getExposure, getStartDate, getEndDate],
  (exposure, startDate, endDate) =>
    exposure.filter((data) => {
      if (data.date >= startDate && data.date <= endDate) {
        return data
      } else {
        return null
      }
    }),
)

export const getFirst = createSelector(
  [getExposure],
  (exposure) => first(exposure),
)

export const getCurrent = createSelector(
  [getExposure],
  (exposure) => last(exposure),
)

export const getFirstHistoricalExposureDate = createSelector(
  [getFilteredExposure],
  (exposure) => get(first(exposure), "date", null),
)

const getSelectedDateFactors = createSelector(
  [getExposureData, getSelectedDate],
  (securityExposure, date) => {
    const history = get(find(securityExposure, d => moment(d.date).isSame(date)), "factors")

    return chain(history)
      .keyBy("id")
      .mapValues(item => ({ value: item.net }))
      .value()
  },
)

export const getAllItems = createSelector(
  [getFactorsMap, getCurrent, getDisabled],
  (factorsMetadata, lastDatum, disabled) =>
    map(factorsMetadata, ({ name, category }, id) => ({
      id,
      label: name,
      category,
      lastValue: get(lastDatum, ["factors", id], 0),
      field: `factors.${id}`,
      disabled: !!disabled[id],
    })),
)

export const getDrift = createSelector(
  [getExposure],
  makeComputeDriftFromData(item => item.factors),
)

export const getData = createSelector(
  [getAllItems, getFilteredExposure],
  (items, exposure) => computeData(items, exposure),
)

export const getNonZerosKeys = createSelector(
  [getData],
  computeNonZerosKeys,
)

export const getDisplayCategories = createSelector(
  [getAllItems, getNonZerosKeys],
  computeDisplayCategories,
)

export const getOptions = createSelector(
  [getCategories, getFirst, getCurrent, getDisplayCategories],
  (categories, first, current, displayCategories) => {
    if (first && current) {
      return filter(categories, category => displayCategories.indexOf(category.id) >= 0)
    }
    return EMPTY_ARRAY
  },
)

export const getSelectedCategory = createSelector(
  [getRawSelectedCategory, getOptions],
  (selectedCategory, categories) => {
    if (find(categories, cat => cat.id === selectedCategory)) {
      return selectedCategory
    }

    return get(categories, "0.id")
  },
)

export const getExposureAttributionData = createSelector(
  [getSelectedDateFactors, getSelectedCategory, getFactorsMap],
  (selectedDateFactors, selectedCategory, factorsMetadata) =>
    chain(selectedDateFactors)
      .map((factor, id) => ({
        id,
        value: factor.value,
        label: factorsMetadata[id].name,
        category: factorsMetadata[id].category,
      }))
      .filter(factor => selectedCategory === factor.category)
      .reduce((accumulator, factor) => {
        if (factor.value > 0) {
          accumulator.positive.push(factor)
        } else if (factor.value < 0) {
          accumulator.negative.push(factor)
        }

        return accumulator
      }, { positive: [], negative: [] })
      .mapValues(factors =>
        chain(factors)
          .sortBy(factor => -Math.abs(factor.value))
          .take(ANALYZE_FACTORS_TAKE)
          .value(),
      )
      .value(),
)

export const getMaxFactors = createSelector(
  [getExposureAttributionData],
  (exposureAttribution) => {
    const topPositive = get(exposureAttribution, "positive[0].value", 0)
    const topNegative = get(exposureAttribution, "negative[0].value", 0)
    return Math.max(topPositive, Math.abs(topNegative))
  },
)
